/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/* eslint-disable react/no-unknown-property */
import React, {Fragment, useEffect, useState, useRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {FormattedMessage, useIntl} from 'react-intl'

import loadable from '@loadable/component'
const PageNotFound = loadable(() => import('./../page-not-found'))

// Components
import {Box, Button, Flex, Stack, Text} from '@chakra-ui/react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {
    useUsid,
    useShopperContext,
    useProduct,
    useProductSearch,
    useShopperCustomersMutation,
    useCustomerId,
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper,
} from '@salesforce/commerce-sdk-react'

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useGetPdpPlus} from '../../custom-api/hooks/use-get-pdp-plus'
import RecommendedProducts from '../../components/recommended-products'
import {PromoSliderVideo} from '../../components/editorial-components'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// constant
import {
    API_ERROR_MESSAGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST,
} from '@salesforce/retail-react-app/app/constants'

import {
    MAX_CACHE_AGE_PDP,
    STALE_WHILE_REVALIDATE_PDP,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
} from '../../../app/constants'

import {
    rebuildPathWithParams,
    removeSiteLocaleFromPath,
} from '@salesforce/retail-react-app/app/utils/url'

import Awards from './partials/awards'
import KeyFeatures from '../../components/editorial-components/key-features'
import OfficinaPage from '../officina'
import ProductCompare from '../../components/product-compare'
import ProductView from '../../components/product-view'
import StickyMenu from './partials/StickyMenu'
// import YoutubeIframe from '../../components/youtube-video'
import {BRAND_LABEL, BRAND_SHORT_LABEL} from '../../brand'
import {Redirect} from 'react-router-dom/cjs/react-router-dom'
import {formatCards} from './formatKeyFeatures'
// import {getDamBaseUrl} from '../../custom-hooks/use-dam-image'
import {
    generateGUID,
    getMetaTags,
    parseAndSanitizeJSONWithSpaces,
    isUntypedTrue,
} from '../../utils/utils'
import {remCalc} from '../../theme/_utils'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useToast} from '../../hooks/use-toast'
import AlternateMetaTag from '../../components/metatag/alternate'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useMutationObserver from '../../custom-hooks/use-mutation-observer'
import {
    addProductToBreadcrumbs,
    getBundleId,
    getPriceData,
    isProductPurchasable,
    shouldShowPageNotFound,
} from '../../utils/product-utils'

import {productUrlBuilder} from '../../utils/url'
import SanitizedHtml from '../../components/html-sanitizer'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'

import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {useWishList} from '../../hooks/use-wish-list'
import {MID} from '../../custom-api/hooks/use-marketing-cloud-tracking'
import {findImageGroupBy} from '../../utils/image-groups-utils'
import WhatsInTheBox from '../../components/whats-in-the-box'
// import Features from '../../components/features'
import {useVariant} from '../../hooks/use-variant'
import usePdpBannerRecovery from './hooks/use-pdp-banner-recovery'
import BenefitsPDP from './partials/benefits-pdp'
import SideCardPDP from '../../components/pdp-side-card/SideCardPDP'
import ReviewsOnPage from '../../components/review/ReviewsOnPage'
import {useCustomPreference} from '../../hooks/use-custom-preference'
import useIntersectionObserver from '../../custom-hooks/use-intersection-observer'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'

const ProductDetail = () => {
    const {formatMessage} = useIntl()

    const history = useHistory()
    const location = useLocation()
    const activeData = useActiveData()
    const toast = useToast()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {addLayer} = useGoogleTagManager()
    const {currency} = useCurrency()
    const {site, locale} = useMultiSite()

    const productViewRef = useRef(null)
    const [isAddToCartLoading, setIsAddToCartLoading] = useState(false)
    const [isSelectionPending, setIsSelectionPending] = useState(false)
    const [orderReviewPriceData, setOrderReviewPriceData] = useState(null)

    const productDetailRef = useRef(null)
    const awardsRef = useRef(null)
    const keyFeaturesRef = useRef(null)
    const specificationsRef = useRef(null)
    const similarRef = useRef(null)
    const isLocationChangeEventSent = useRef(null)

    const isBraun = BRAND_SHORT_LABEL === 'BR'

    const {customParams} = getConfig()
    const {buildLocalizedUrl} = useMulticountryUtils()
    const wazzupConfig = useCustomPreference('wazzupConfiguration')

    /****************************** Basket *********************************/
    const {data: basket, isLoading: isBasketLoading, refetch: refetchBasket} = useCurrentBasket()
    const createBasket = useShopperBasketsMutation('createBasket')
    const addItemToBasketMutation = useShopperBasketsMutation('addItemToBasket')
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()

    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_PDP}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_PDP}`,
        )
    }

    /*************************** Product Detail and Category ********************/
    const {urlSegment} = useParams()
    const safeDecode = (value) =>
        typeof value === 'string' ? decodeURIComponent(value)?.replaceAll('+', ' ') : null
    const productId = safeDecode(urlSegment.match(/([A-Za-z0-9.%+-]+)\.html/)?.[1])

    const urlParams = new URLSearchParams(location.search)
    const isProductQueryEnabled = !!urlParams.get('pid') || !!productId

    const {
        data: product,
        isFetching: isProductFetching,
        isFetched: isProductFetched,
        isError: isProductError,
        refetch: refetchProduct,
    } = useProduct(
        {
            parameters: {
                id: urlParams.get('pid') || productId,
                perPricebook: true,
                expand: ['availability', 'images', 'prices', 'promotions'],
                locale: locale.id,
                currency,
            },
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true,
            enabled: isProductQueryEnabled,
        },
    )

    /**
     * Flexible Bundle detection
     * A "Flexible Bundle" is a virtual SFCC Product Set with no editorial
     * custom attributes of its own; per requirement,
     * all editorial content on its PDP (reviews, key features, benefit, sidecard, etc.) must
     * be inherited from the bundle's first "primary" product.
     */
    const isFlexibleBundle =
        product?.type?.set && product?.c_isBundle === 'true' && product?.c_bundleType === 'Flexible'
    const firstPrimaryProductId = isFlexibleBundle
        ? product?.c_bundleProductsInfo?.flexibleBundles?.primaryProducts?.[0]?.productId
        : null

    // `firstPrimaryProduct` on the bundle is only a lightweight pricing/PDP-card projection
    // (see buildBundleProductData in the backend), not the full product document with the
    // editorial custom attributes the sections below need. Fetch the full record via useProduct,
    // enabled only when we have a primary product id to look up.
    const {data: editorialProduct} = useProduct(
        {
            parameters: {
                id: firstPrimaryProductId,
                perPricebook: true,
                expand: ['availability', 'images', 'prices', 'promotions'],
                locale: locale.id,
                currency,
            },
        },
        {
            keepPreviousData: true,
            enabled: !!firstPrimaryProductId,
        },
    )

    const editorialSourceProduct = isFlexibleBundle ? editorialProduct : product

    const shouldAttemptPdpRecovery = Boolean(isProductError && !urlParams.get('pid') && productId)
    const {
        data: recoveryProductSearch,
        isFetching: isRecoveryProductSearchFetching,
        isFetched: isRecoveryProductSearchFetched,
    } = useProductSearch(
        {
            parameters: {
                q: productId,
                limit: 1,
                perPricebook: true,
                locale: locale.id,
                site: site.id,
                currency,
            },
        },
        {
            enabled: shouldAttemptPdpRecovery,
            keepPreviousData: false,
        },
    )

    const buttonContainerRef = useRef(null)

    const isCtaVisible = useIntersectionObserver(buttonContainerRef, {
        useOnce: false,
        threshold: 0,
        rootMargin: '0px',
    })

    const showStickyMenu = !isCtaVisible

    /**************** Personalization *************************/
    const [hasInjectedContent, setHasInjectedContent] = useState(false)
    const [hasFbtInjectedContent, setHasFbtInjectedContent] = useState(false)

    const recommendationsContainerRef = useRef(null)
    const fbtRecommendationsContainerRef = useRef(null)

    useMutationObserver(
        recommendationsContainerRef,
        (mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.target.childNodes.length > 0) {
                    setHasInjectedContent(true)
                    return
                }
            }
            setHasInjectedContent(false)
        },
        {
            childList: true,
        },
    )

    useMutationObserver(
        fbtRecommendationsContainerRef,
        (mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'childList' && mutation.target.childNodes.length > 0) {
                    setHasFbtInjectedContent(true)
                    return
                }
            }
            setHasFbtInjectedContent(false)
        },
        {
            childList: true,
        },
    )

    const c_breadcrumbs = useMemo(() => {
        if (product?.c_breadcrumbs) return addProductToBreadcrumbs(product)
    }, [product?.c_breadcrumbs])

    const isOfficina = product?.c_isOfficina === 'true'

    const variant = useVariant(product)

    const gtmFiredRef = useRef(false)

    useEffect(() => {
        return () => {
            gtmFiredRef.current = false
        }
    }, [location.pathname])

    /**************** Product Variant ****************/
    useEffect(() => {
        if (!variant) {
            return
        }
        // update the variation attributes parameter on
        // the url accordingly as the variant changes
        const updatedUrl = rebuildPathWithParams(`${location.pathname}${location.search}`, {
            pid: variant?.productId,
        })
        if (urlParams.get('pid') !== variant?.productId) {
            history.replace(updatedUrl)
        }
    }, [variant])

    /**************** Corporate ****************/
    const {usid} = useUsid()
    const {data: shopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    usePdpBannerRecovery({
        productId,
        pid: urlParams.get('pid'),
        isProductQueryEnabled,
        isProductFetched,
        isProductFetching,
        hasBannerInfo: !!product?.c_bannerInfo,
        basketId: basket?.basketId,
        isBasketLoading,
        shopperContext,
        createBasket,
        refetchBasket,
        refetchProduct,
        pathname: location.pathname,
        search: location.search,
    })

    /**************** PdpPlus Components ****************/
    const {data: pdpPlusData} = useGetPdpPlus(productId, {enabled: !!productId})

    const stickyOmnibusTooltipText = useMemo(() => {
        if (!pdpPlusData) {
            return null
        }

        const directKeys = [
            'sticky_bottom_banner_omnibus_tooltip',
            'sticky_banner_omnibus_tooltip',
            'omnibus_tooltip_text',
            'omnibus_tooltip',
        ]

        const directMatch = directKeys.map((key) => pdpPlusData?.[key]).find(Boolean)
        if (directMatch) {
            return directMatch
        }

        return (
            pdpPlusData?.static_labels?.sticky_bottom_banner_omnibus_tooltip ||
            pdpPlusData?.static_labels?.omnibus_tooltip_text ||
            null
        )
    }, [pdpPlusData])

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem',
    )

    const handleAddToWishlist = (product, variant, quantity) => {
        const isItemInWishlist = wishlist?.customerProductListItems?.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id,
        )

        if (!isItemInWishlist) {
            createCustomerProductListItem.mutate(
                {
                    parameters: {
                        listId: wishlist.id,
                        customerId,
                    },
                    body: {
                        // NOTE: API does not respect quantity, it always adds 1
                        quantity,
                        productId: variant?.productId || product?.id,
                        public: true,
                        priority: 1,
                        type: 'product',
                    },
                },
                {
                    onSuccess: () => {
                        toast({
                            title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                            status: 'success',
                        })
                    },
                    onError: () => {
                        showError()
                    },
                },
            )
        } else {
            toast({
                title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                status: 'info',
                action: (
                    <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                        {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                    </Button>
                ),
            })
        }
    }

    /**************** Add To Cart ****************/
    const {onOpen: onAddToCartModalOpen} = useAddToCartModalContext()
    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
        })
    }

    const handleAddToCart = async (productSelectionValues, knownBasketId) => {
        try {
            const productItems = productSelectionValues.map(
                ({
                    variant,
                    quantity = 1,
                    product,
                    cartCustomFields = {},
                    customAttributes = {},
                }) => {
                    const productId = variant?.productId || product?.productId || product?.id

                    const price = variant?.price ?? product?.price

                    return {
                        productId,
                        price,
                        quantity,
                        c_MaterialGroup: product?.c_materialGroup,
                        c_categoryName: product?.c_categoryName,
                        c_subModelIdentifier: product?.c_subModelIdentifier,
                        c_prdhaCategory: product?.c_prdhaCategory,
                        ...cartCustomFields,
                        ...customAttributes,
                    }
                },
            )

            // When a basketId is already known (e.g. from an earlier add within the same
            // add-to-cart flow), add directly to that basket. addItemToNewOrExistingBasket
            // decides whether to create or reuse a basket based on a snapshot of customer
            // basket data captured at render time, which never updates mid-flow. Reusing it
            // for every sequential recommendation/warranty add caused a second createBasket
            // call (and a "Something went wrong" failure) once the first add had already
            // created the basket.
            const basketResult = knownBasketId
                ? await addItemToBasketMutation.mutateAsync({
                      parameters: {basketId: knownBasketId},
                      body: productItems,
                  })
                : await addItemToNewOrExistingBasket(productItems)

            // einstein.sendAddToCart(productItems)

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return {items: productSelectionValues, basketId: basketResult?.basketId}
        } catch (error) {
            console.error('error', error)
            showError(error)
            return null
        }
    }

    // Full product record for whichever FBT item was last clicked; the add-to-cart modal
    // needs it to render the item (the widget only gives us a productId).
    const [fbtClickedProductId, setFbtClickedProductId] = useState(null)
    const {data: fbtClickedProduct} = useProduct(
        {
            parameters: {
                id: fbtClickedProductId,
                perPricebook: true,
                expand: ['availability', 'images', 'prices', 'promotions'],
                locale: locale.id,
                currency,
            },
        },
        {enabled: !!fbtClickedProductId},
    )

    useEffect(() => {
        if (!fbtClickedProduct) return
        const variant = fbtClickedProduct?.c_variants?.[0] || fbtClickedProduct?.variants?.[0]

        handleAddToCart([{product: fbtClickedProduct, variant, quantity: 1}]).then((result) => {
            if (result?.items) {
                onAddToCartModalOpen({itemsAdded: result.items, selectedQuantity: 1})
            }
        })
        setFbtClickedProductId(null)
    }, [fbtClickedProduct])

    // Delegated listener catches clicks on FBT "add to cart" buttons injected by the third party/test component
    useEffect(() => {
        const container = fbtRecommendationsContainerRef.current
        if (!container) return undefined

        const handleFbtAddToCartClick = (event) => {
            const button = event.target.closest('.js-cart-add')
            if (!button || !container.contains(button)) return
            const productId = button.getAttribute('data-product-code')
            if (!productId) return

            // Trigger the product fetch above, which adds to cart once resolved.
            setFbtClickedProductId(productId)
        }

        container.addEventListener('click', handleFbtAddToCartClick)
        return () => container.removeEventListener('click', handleFbtAddToCartClick)
    }, [hasFbtInjectedContent])

    /**************** GTM Event ****************/
    // This event is fired only for SKUs
    useEffect(() => {
        if (!gtmFiredRef.current) {
            if (product && productId !== product?.id) {
                window.dispatchEvent(new Event('productDetailLoaded'))
                addLayer(polish_ecommerce_gtm_event.bind(this))
                addLayer(
                    item_gtm_event.bind(this, {
                        event: 'ga_event',
                        event_name: 'view_item',
                        bundle: product?.c_isBundle,
                        bundle_id: getBundleId(product),
                        currency: currency,
                        items: [
                            {
                                item_name: product?.c_defaultName,
                                item_id: product?.id,
                                item_category: product?.c_defaultCategoryName,
                                item_variant: product?.c_subModelIdentifier,
                                price: +(variant?.price / (1 + product?.c_taxRate / 100)).toFixed(
                                    2,
                                ),
                                fullprice: getPriceData(product)?.isOnSale
                                    ? 'reduced'
                                    : 'fullprice',
                                rating_points: '',
                                // get_reevoo_rating() || get_bv_rating(),
                                erp_category: product?.c_materialGroup,
                                prdha_category: product?.c_prdha_category,
                                stock_status:
                                    product?.inventory?.stockLevel > 0
                                        ? 'in-stock'
                                        : 'out-of-stock',
                            },
                        ],
                    }),
                )
                gtmFiredRef.current = true

                if (isLocationChangeEventSent.current !== location.pathname) {
                    setTimeout(() => {
                        //console.log('[SFCC] Location change PDP')
                        window.dispatchEvent(new Event('locationchange'))
                    }, 150)
                    isLocationChangeEventSent.current = location.pathname
                }

                if (window._etmc) {
                    window._etmc.push(['setOrgId', MID])
                    window._etmc.push(['trackPageView', {item: product?.id}])
                }
            }
        }
    }, [product])

    /**************** Einstein ****************/
    useEffect(() => {
        // TODO : attualmente esclusi i flexible ma da capire come va gestito
        if (product && product.type.set && product.c_bundleType !== 'Flexible') {
            // einstein.sendViewProduct(product)
            const childrenProducts = product.setProducts
            childrenProducts.map((child) => {
                try {
                    // einstein.sendViewProduct(child)
                } catch (err) {
                    logger.error('Einstein sendViewProduct error', {
                        namespace: 'ProductDetail.useEffect',
                        additionalProperties: {error: err, child},
                    })
                }
                activeData.sendViewProduct({id: product?.primaryCategoryId}, child, 'detail')
            })
        } else if (product) {
            try {
                // einstein.sendViewProduct(product)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product},
                })
            }
            activeData.sendViewProduct({id: product?.primaryCategoryId}, product, 'detail')
        }
    }, [product])

    /***************** Wazzup *****************/
    useEffect(() => {
        if (product && wazzupConfig) {
            window?.WazzUp?.runSendDatas()
        }
    }, [product])

    let featureCards
    /********KEY FEATURES********/
    // For Flexible Bundles, key features/benefits are inherited from the primary product.
    if (editorialSourceProduct?.[isBraun ? 'c_benefit' : 'c_keyFeature']) {
        let parsedFeatures = parseAndSanitizeJSONWithSpaces(
            editorialSourceProduct?.[isBraun ? 'c_benefit' : 'c_keyFeature'],
            editorialSourceProduct?.c_subModelIdentifier,
        )
        featureCards = formatCards(parsedFeatures)
    }

    /**
     * SEO Redirects
     */

    const pathnameNoLocale = removeSiteLocaleFromPath(location.pathname)

    const redirectFunctionality = (redirectUrl) => {
        try {
            if (res) {
                res.status(301).location(redirectUrl)
                return
            } else {
                return (
                    <Redirect
                        to={{
                            pathname: redirectUrl,
                        }}
                    />
                )
            }
        } catch (e) {
            console.error(e)
        }
    }

    if (shouldAttemptPdpRecovery && isRecoveryProductSearchFetched) {
        const recoveryHit = recoveryProductSearch?.hits?.[0]
        const recoveryProduct = recoveryHit?.representedProduct || recoveryHit
        const recoveryPid =
            recoveryProduct?.id || recoveryProduct?.productId || recoveryHit?.productId || productId
        const recoveryBasePath = productUrlBuilder(recoveryProduct)

        if (recoveryBasePath && recoveryPid) {
            const recoverySeparator = recoveryBasePath.includes('?') ? '&' : '?'
            const recoveryTarget = `/${
                locale.alias
            }${recoveryBasePath}${recoverySeparator}pid=${encodeURIComponent(recoveryPid)}`
            const currentPathAndSearch = `${location.pathname}${location.search}`

            if (recoveryTarget !== currentPathAndSearch) {
                return redirectFunctionality(recoveryTarget)
            }
        }
    }

    if (
        product?.c_variationGroup_slugUrl &&
        decodeURIComponent(product?.c_variationGroup_slugUrl) !==
            decodeURIComponent(pathnameNoLocale) &&
        product?.c_variationGroup_slugUrl !== pathnameNoLocale
    ) {
        // SKU
        const redirectUrl = `/${locale.alias}${product?.c_variationGroup_slugUrl}${location.search}`
        return redirectFunctionality(redirectUrl)
    } else if (
        !product?.c_variationGroup_slugUrl &&
        product?.c_slugUrl &&
        decodeURIComponent(product?.c_slugUrl) !== decodeURIComponent(pathnameNoLocale) &&
        product?.c_slugUrl !== pathnameNoLocale
    ) {
        // Variation
        const redirectUrl = `/${locale.alias}${product?.c_slugUrl}${location.search}`
        return redirectFunctionality(redirectUrl)
    }

    /**************** Error Handling ****************/

    // Avoid flipping to 404 when a valid product is already rendered and
    // a follow-up request (for canonical/pid sync) fails transiently.
    const hasLoadedProductData = Boolean(product?.id || product?.productId)
    const shouldTreatAsProductError =
        isProductError &&
        !hasLoadedProductData &&
        !(shouldAttemptPdpRecovery && isRecoveryProductSearchFetching)

    const show404 = shouldShowPageNotFound({
        isProductError: shouldTreatAsProductError,
        isProductQueryEnabled,
        isProductFetching,
        product,
    })

    if (show404) {
        if (res) res.status(404)
        return <PageNotFound />
    }

    if (isOfficina) return <OfficinaPage product={product} />

    /**************** SideCard ****************/

    let urlCanonical = buildLocalizedUrl(`${productUrlBuilder(product)}`)
    const pid = urlParams.get('pid')
    if (pid) urlCanonical += `?pid=${pid}`
    else if (variant) urlCanonical += `?pid=${variant?.productId}`

    const recommendations = (product?.c_recommendations || []).filter(
        (rec) => rec?.recommendationType?.value === 2,
    )

    const stripHtmlTags = (str) => (str ? str.replace(/<[^>]*>/g, '') : '')
    const cleanDescription = stripHtmlTags(product?.longDescription || product?.shortDescription)

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: product?.name,
        description: cleanDescription,
        url: `https://${customParams.currentHost}${urlCanonical}`,
    }

    const schemaImageLink = findImageGroupBy(product?.imageGroups, {
        viewType: 'medium',
        selectedVariationAttributes: product?.variationValues,
    })?.images?.[0]?.link

    const availability =
        (product?.c_skuIsPDPOnly === true || product?.c_skuIsPDPOnly === 'true') &&
        !isProductPurchasable(product)
            ? 'https://schema.org/Discontinued'
            : !product?.inventory?.orderable || product?.inventory?.orderable === 'false'
            ? 'https://schema.org/OutOfStock'
            : 'https://schema.org/InStock'

    const productSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: product?.name,
        image: {
            '@type': 'ImageObject',
            contentUrl: schemaImageLink,
            creditText: `De'Longhi Appliances S.r.l`,
        },
        brand: {'@type': 'Brand', name: BRAND_LABEL},
        offers: {
            '@type': 'Offer',
            url: `https://${customParams.currentHost}${urlCanonical}`,
            priceCurrency: currency,
            price: variant?.price,
            availability: availability,
        },
    }

    const categories = product?.c_breadcrumbs || []

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: categories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }

    const imageObjectSchemaMarkup = {
        '@type': 'ImageObject',
        contentUrl: schemaImageLink || '',
        creditText: `De'Longhi Appliances S.r.l`,
    }

    // Parse "What's in the box" JSON attribute
    let whatsInTheBoxItems = []
    if (product?.c_whatsInTheBox) {
        try {
            whatsInTheBoxItems = JSON.parse(product.c_whatsInTheBox)
        } catch (e) {
            whatsInTheBoxItems = []
        }
    }
    if (!whatsInTheBoxItems.length && product?.c_whatsInThePackImages) {
        try {
            const parsedPackImages = JSON.parse(product.c_whatsInThePackImages)
            whatsInTheBoxItems = (Array.isArray(parsedPackImages) ? parsedPackImages : []).map(
                (item, index) => ({
                    title: item?.title || item?.code || `Box item ${index + 1}`,
                    imageLink: item?.imageLink || item?.medialink || item?.url || item?.link,
                }),
            )
        } catch (e) {
            whatsInTheBoxItems = []
        }
    }
    const handleFocusLinkClick = (dataAbValue) => {
        const selector = `[data-ab="${dataAbValue}"]`
        const targetElement = document.querySelector(selector)

        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        } else {
            console.warn(`[ProductDetail] Scroll target not found for selector: ${selector}`)
        }
    }

    const feefoReviewOnPageParentSku =
        editorialSourceProduct?.c_prdha_category ||
        editorialSourceProduct?.c_primaryProductMasterId ||
        editorialSourceProduct?.c_subModelIdentifier ||
        editorialSourceProduct?.id

    return (
        <Box
            style={{position: 'relative'}}
            bg="background.base.default"
            className="sf-product-detail-page"
            data-testid="product-details-page"
            data-gtm="product-details-page"
            ref={productDetailRef}
            overflowX={{base: 'hidden', lg: 'clip'}}
        >
            <Helmet>
                {getMetaTags(product?.c_pageMetaTagRules)}
                {isFlexibleBundle && <meta name="robots" content="noindex" />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(productSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(imageObjectSchemaMarkup, null, 2)}
                </script>
                <link rel="canonical" href={`https://${customParams.currentHost}${urlCanonical}`} />
            </Helmet>
            {product?.c_hreflangs && (
                <AlternateMetaTag links={product.c_hreflangs} pid={urlParams.get('pid')} />
            )}
            <Box
                data-testid="sticky-menu-slider"
                aria-hidden={!showStickyMenu}
                position="fixed"
                left={0}
                right={0}
                bottom={0}
                zIndex={11}
                transform={showStickyMenu ? 'translate3d(0, 0, 0)' : 'translate3d(0, 100%, 0)'}
                opacity={showStickyMenu ? 1 : 0}
                pointerEvents={showStickyMenu ? 'auto' : 'none'}
                transition="transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out"
                willChange="transform, opacity"
            >
                <StickyMenu
                    product={product}
                    renderActionButtons={productViewRef.current?.renderActionButtons}
                    isAddToCartLoading={isAddToCartLoading}
                    isSelectionPending={isSelectionPending}
                    isHidden={!showStickyMenu}
                    omnibusTooltipText={stickyOmnibusTooltipText}
                    orderReviewPriceData={orderReviewPriceData}
                />
            </Box>
            {!isUntypedTrue(shopperContext?.customQualifiers?.isCorporate) &&
                pdpPlusData?.top_editorial_components?.map((component) => (
                    <EditorialComponentsFactory
                        key={component.uid}
                        componentType={component?._content_type_uid}
                        {...component}
                        data-ab={`editorial-top-${component?._content_type_uid}`}
                        data-cs-label={`editorial-top-${component?._content_type_uid}`}
                    />
                ))}
            <Stack gap={0}>
                <Fragment>
                    <Box layerStyle="page">
                        <ProductView
                            // Do not use an arrow function as we are manipulating the functions scope.
                            ref={function (node) {
                                // we need the ref to the node to determine if the product view is in view
                                // and we also get the renderActionButtons function to use it in the sticky menu
                                // it is made available here with the `this` keyword because it is bound to this scope from inside the ProductView component
                                productViewRef.current = {
                                    node,
                                    renderActionButtons: this.renderActionButtons,
                                    cartAddButtonRef: this.cartAddButtonRef,
                                    notifyMeContainerRef: this.notifyMeContainerRef,
                                    shouldShowNotifyMe: this.shouldShowNotifyMe,
                                    buttonContainerRef: this.buttonContainerRef,
                                }
                                //
                                buttonContainerRef.current = this.buttonContainerRef?.current
                            }}
                            product={product}
                            // Full primary-product document (Flexible Bundle only) so ProductView
                            // can source technical specifications from it instead of the bundle's
                            // own (empty) c_groupedTechSpecs
                            editorialProduct={editorialSourceProduct}
                            category={c_breadcrumbs || []}
                            addToCart={async (
                                variant,
                                quantity,
                                recommendedProducts = [],
                                mainItemsOverride = null,
                            ) => {
                                // For a bundle (fixed or flexible), `mainItemsOverride` already contains
                                // every purchasable line item (primary product(s) + selected accessories).
                                // Only fall back to the single PDP `mainItem` for a regular, non-bundle
                                // product. This ensures the Product Set/bundle master product itself is
                                // never submitted to the add-to-basket API (SFCC rejects type 'set').
                                const parentProductId = variant?.productId || product?.id

                                const hasSelectedWarranty = (recommendedProducts || []).some(
                                    (recommendedProduct) =>
                                        recommendedProduct?.__isWarrantySelection === true ||
                                        Boolean(
                                            recommendedProduct?.warrantyGroupName ||
                                                recommendedProduct?.warrantyExtensionPeriod ||
                                                recommendedProduct?.rawRec?.recommendationType
                                                    ?.value === 5,
                                        ),
                                )
                                const warrantyUUID = hasSelectedWarranty ? generateGUID() : null
                                const parentVariationGroup =
                                    variant?.c_subModelIdentifier || product?.c_subModelIdentifier
                                const mainItem = {
                                    product,
                                    variant,
                                    quantity,
                                    customAttributes: warrantyUUID
                                        ? {c_UUIDWarranty: warrantyUUID}
                                        : {},
                                }
                                const mainItems = mainItemsOverride || [mainItem]

                                const mainItemProductIds = new Set(
                                    mainItems
                                        .map(
                                            (item) =>
                                                item?.variant?.productId ||
                                                item?.product?.productId ||
                                                item?.product?.id,
                                        )
                                        .filter(Boolean),
                                )

                                const droppedRecommendations = []

                                const recommendedItems = (recommendedProducts || [])
                                    .map((recommendedProduct) => {
                                        const explicitRecommendedProductId =
                                            recommendedProduct?.productId || recommendedProduct?.id
                                        const recommendedVariant =
                                            recommendedProduct?.c_variants?.[0] ||
                                            recommendedProduct?.variants?.[0]
                                        const isWarrantyRecommendation =
                                            recommendedProduct?.__isWarrantySelection === true ||
                                            Boolean(
                                                recommendedProduct?.warrantyGroupName ||
                                                    recommendedProduct?.warrantyExtensionPeriod ||
                                                    recommendedProduct?.rawRec?.recommendationType
                                                        ?.value === 5,
                                            )
                                        const recommendedProductId = isWarrantyRecommendation
                                            ? recommendedProduct?.productId ||
                                              recommendedVariant?.productId
                                            : // Master products (with variants) can't be added to
                                              // a basket directly; resolve to the first available
                                              // variant SKU instead of the non-purchasable master id.
                                              recommendedVariant?.productId ||
                                              explicitRecommendedProductId ||
                                              recommendedVariant?.id
                                        const warrantyParentProductId =
                                            recommendedProduct?.c_parentProductId || parentProductId

                                        return {
                                            product: recommendedProduct,
                                            variant: {
                                                productId: recommendedProductId,
                                                // The warranty's own price already reflects the plan
                                                // the shopper selected; recommendedVariant would only
                                                // ever be the group's first (default) plan.
                                                price: isWarrantyRecommendation
                                                    ? recommendedProduct?.price ??
                                                      recommendedVariant?.price
                                                    : recommendedVariant?.price ??
                                                      recommendedProduct?.price,
                                            },
                                            quantity: 1,
                                            customAttributes: isWarrantyRecommendation
                                                ? {
                                                      c_isWarranty: true,
                                                      c_parentProductId: warrantyParentProductId,
                                                      ...(warrantyUUID ||
                                                      recommendedProduct?.c_UUIDWarranty
                                                          ? {
                                                                c_UUIDWarranty:
                                                                    warrantyUUID ||
                                                                    recommendedProduct?.c_UUIDWarranty,
                                                            }
                                                          : {}),
                                                      ...(parentVariationGroup
                                                          ? {
                                                                c_subModelIdentifier:
                                                                    parentVariationGroup,
                                                            }
                                                          : {}),
                                                      ...(recommendedProduct?.warrantyGroupName
                                                          ? {
                                                                c_serviceGroupName:
                                                                    recommendedProduct.warrantyGroupName,
                                                            }
                                                          : {}),
                                                      ...(recommendedProduct?.warrantyExtensionPeriod ||
                                                      recommendedProduct?.serviceDuration
                                                          ? {
                                                                c_serviceDuration: Number(
                                                                    recommendedProduct?.warrantyExtensionPeriod ||
                                                                        recommendedProduct?.serviceDuration,
                                                                ),
                                                            }
                                                          : {}),
                                                  }
                                                : {},
                                        }
                                    })
                                    .filter((item) => {
                                        const sourceId =
                                            item?.product?.id || item?.product?.productId
                                        if (!item?.variant?.productId) {
                                            droppedRecommendations.push({
                                                sourceId,
                                                reason: 'no-resolved-productId',
                                            })
                                            return false
                                        }
                                        if (mainItemProductIds.has(item.variant.productId)) {
                                            // Never resubmit a product that's already part of
                                            // mainItems (e.g. a bundle accessory that is also
                                            // surfaced in the general recommendations carousel).
                                            droppedRecommendations.push({
                                                sourceId,
                                                resolvedProductId: item.variant.productId,
                                                reason: 'duplicates-main-item',
                                            })
                                            return false
                                        }

                                        // A recommended item's own aggregate inventory is the only
                                        // stock signal we have; skip it rather than send an
                                        // out-of-stock SKU that SFCC would silently drop (and that
                                        // can later break unrelated basket mutations, e.g. removal).
                                        const inventory = item?.product?.inventory
                                        const isOutOfStock = inventory
                                            ? Number(inventory.stockLevel || 0) <= 0 ||
                                              inventory.orderable === false
                                            : false

                                        if (isOutOfStock) {
                                            droppedRecommendations.push({
                                                sourceId,
                                                resolvedProductId: item.variant.productId,
                                                reason: 'out-of-stock',
                                            })
                                            return false
                                        }

                                        return true
                                    })

                                const uniqueRecommendedItems = Array.from(
                                    new Map(
                                        recommendedItems.map((item) => {
                                            const recommendationTypeKey = item?.customAttributes
                                                ?.c_isWarranty
                                                ? 'warranty'
                                                : 'recommendation'
                                            return [
                                                `${recommendationTypeKey}:${item?.variant?.productId}`,
                                                item,
                                            ]
                                        }),
                                    ).values(),
                                )

                                if (
                                    droppedRecommendations.length ||
                                    recommendedItems.length !== uniqueRecommendedItems.length
                                ) {
                                    console.warn('Recommendations excluded before add-to-cart', {
                                        received: recommendedProducts?.length || 0,
                                        keptAfterFilter: recommendedItems.length,
                                        keptAfterDedupe: uniqueRecommendedItems.length,
                                        droppedRecommendations,
                                    })
                                }

                                const warrantyItems = uniqueRecommendedItems.filter(
                                    (item) => item?.customAttributes?.c_isWarranty === true,
                                )
                                const nonWarrantyItems = uniqueRecommendedItems.filter(
                                    (item) => item?.customAttributes?.c_isWarranty !== true,
                                )

                                if (warrantyItems.length) {
                                    console.warn(
                                        'Warranty item(s) being added to the basket',
                                        warrantyItems.map((item) => ({
                                            selectedFromPdp: item?.product?.name,
                                            productId: item?.variant?.productId,
                                            price: item?.variant?.price,
                                            serviceDuration:
                                                item?.customAttributes?.c_serviceDuration,
                                        })),
                                    )
                                }

                                // Add the main PDP item(s) (a single product, or every purchasable item
                                // in a bundle) together with any selected warranty in a single request
                                // so SFCC creates the linked pair correctly.
                                const primaryResult = await handleAddToCart([
                                    ...mainItems,
                                    ...warrantyItems,
                                ])

                                if (!primaryResult) {
                                    return null
                                }

                                // Reuse the basket created/used by the primary add for every
                                // subsequent recommendation add below, so we never re-trigger the
                                // "create a new basket" path once a basket already exists.
                                const activeBasketId = primaryResult.basketId || basket?.basketId
                                const addedRecommendations = []
                                const failedRecommendations = []

                                // Add recommendations one-by-one so one failing SKU doesn't block cart add.
                                for (const item of nonWarrantyItems) {
                                    // eslint-disable-next-line no-await-in-loop
                                    const added = await handleAddToCart([item], activeBasketId)
                                    if (added?.items?.length) {
                                        addedRecommendations.push(...added.items)
                                    } else {
                                        failedRecommendations.push(item?.variant?.productId)
                                    }
                                }

                                if (failedRecommendations.length) {
                                    console.error(
                                        'Some recommended products could not be added to the basket',
                                        failedRecommendations,
                                    )
                                }

                                return [...primaryResult.items, ...addedRecommendations]
                            }}
                            isCorporate={isUntypedTrue(
                                shopperContext?.customQualifiers?.isCorporate,
                            )}
                            pdpPlusData={pdpPlusData}
                            addToWishlist={handleAddToWishlist}
                            isProductLoading={!isProductFetched}
                            isBasketLoading={isBasketLoading}
                            isCategoryLoading={!isProductFetched}
                            isWishlistLoading={isWishlistLoading}
                            isBraun={isBraun}
                            onFocusLinkClick={handleFocusLinkClick}
                            specificationsRef={specificationsRef}
                            onAddToCartLoadingChange={setIsAddToCartLoading}
                            isSelectionPending={isSelectionPending}
                            setIsSelectionPending={setIsSelectionPending}
                            onOrderReviewPriceDataChange={setOrderReviewPriceData}
                        />
                    </Box>

                    {editorialSourceProduct?.[isBraun ? 'c_benefit' : 'c_keyFeature'] && (
                        <Box layerStyle="page">
                            <Box ref={keyFeaturesRef} data-ab="editorial-key-features">
                                <KeyFeatures
                                    categoryName={editorialSourceProduct?.c_categoryName}
                                    cards={featureCards}
                                    isSlider
                                    aspectRatio="4/3"
                                />
                            </Box>
                        </Box>
                    )}

                    {editorialSourceProduct?.c_award && (
                        <Box ref={awardsRef} data-ab="editorial-awards">
                            <Awards
                                awards={editorialSourceProduct.c_award}
                                p={editorialSourceProduct?.c_subModelIdentifier}
                            />
                        </Box>
                    )}

                    {editorialSourceProduct?.c_benefit && !isBraun && (
                        <Box
                            paddingY={{
                                base: 'small-1.base',
                                md: 'small-1.md',
                                lg: 'medium.lg',
                                xl: 'medium.xl',
                            }}
                        >
                            <BenefitsPDP
                                benefits={parseAndSanitizeJSONWithSpaces(
                                    editorialSourceProduct.c_benefit,
                                )}
                            />
                        </Box>
                    )}

                    <Box layerStyle="page" data-ab="compare">
                        <ProductCompare product={editorialSourceProduct} />
                    </Box>

                    <SideCardPDP product={editorialSourceProduct} />

                    <SideCardPDP product={editorialSourceProduct} useProductFields={true} />

                    {editorialSourceProduct?.c_youtubeVideoHowTo && (
                        <PromoSliderVideo
                            abTitle="editorial-howto"
                            sliderGroup={parseAndSanitizeJSONWithSpaces(
                                editorialSourceProduct?.c_youtubeVideoHowTo,
                                editorialSourceProduct?.c_subModelIdentifier,
                            )?.map((video) => ({
                                text_block: {
                                    title: video.title,
                                },
                                video_link: {
                                    href: video.videolink,
                                },
                                thumbnail: video.medialink ? {src: video.medialink} : {},
                            }))}
                            contentType="howto"
                        />
                    )}

                    {/* reviews */}
                    {feefoReviewOnPageParentSku && (
                        <ReviewsOnPage parentSku={feefoReviewOnPageParentSku} />
                    )}

                    {editorialSourceProduct && (
                        <SideCardPDP product={editorialSourceProduct} buttonVariant={true} />
                    )}

                    <Flex flexDir={isBraun ? 'column-reverse' : 'column'} layerStyle="page">
                        <Flex
                            background="background.light"
                            justifyContent="center"
                            id="pdp_product_recommendation_fbt"
                            ref={fbtRecommendationsContainerRef}
                            overflowX="hidden"
                        />
                        {!hasFbtInjectedContent && (
                            <Flex
                                background="background.light"
                                justifyContent="center"
                                width={{
                                    base: `calc(100% + ${remCalc(12)})`,
                                    lg: `calc(100% + ${remCalc(40)})`,
                                    xl: '100%',
                                }}
                                marginRight={{base: remCalc(-12), lg: remCalc(-40), xl: '0'}}
                            >
                                <RecommendedProducts
                                    products={recommendations.map((rec) => rec.recommendedItemId)}
                                    title={
                                        <FormattedMessage
                                            defaultMessage="Frequently Bought Together"
                                            id="recommended_product_card_variant.title"
                                        />
                                    }
                                    variant={'greyBackgroundVariant'}
                                    tileImageVariant={'transparent'}
                                    prefix="frequently-bought-together"
                                    gtmCarouselName="carousel_pdp"
                                />
                            </Flex>
                        )}
                        {/* Render What's in the box only for KW and BR brand.
                            TODO: check if this attribute is filled only on BR/KW products
                            (hence the specific check for brand is not needed) */}
                        {['BR', 'KW'].includes(BRAND_SHORT_LABEL) &&
                            whatsInTheBoxItems.length > 0 && (
                                <Flex justifyContent="center">
                                    <WhatsInTheBox
                                        items={whatsInTheBoxItems}
                                        variant={!isBraun ? 'greyBackgroundVariant' : ''}
                                    />
                                </Flex>
                            )}
                    </Flex>

                    <Box ref={similarRef} layerStyle="page">
                        <Flex
                            justifyContent="center"
                            id="pdp_product_recommendations_ymal"
                            ref={recommendationsContainerRef}
                        />
                        {!hasInjectedContent && (
                            <Flex
                                justifyContent="center"
                                id="pdp_product_recommendations_ymal_cust"
                                width={{
                                    base: `calc(100% + ${remCalc(12)})`,
                                    lg: `calc(100% + ${remCalc(40)})`,
                                    xl: '100%',
                                }}
                                marginRight={{base: remCalc(-12), lg: remCalc(-40), xl: '0'}}
                            >
                                <RecommendedProducts
                                    personalizationId="pdp_product_recommendations_ymal"
                                    products={(editorialSourceProduct?.c_recommendations || [])
                                        .filter((rec) => rec?.recommendationType?.value === 1)
                                        .map((rec) => rec.recommendedItemId)}
                                    tileImageVariant={'transparent'}
                                    title={
                                        <FormattedMessage
                                            defaultMessage="You may also like"
                                            id="may_also_like_product_card.title"
                                        />
                                    }
                                    prefix="ymal"
                                    gtmCarouselName="carousel_pdp"
                                />
                            </Flex>
                        )}
                    </Box>

                    {/* {(product?.c_sidecardImage || generateDescriptionWithBullets(product)) && (
                        <SideCard
                            imageGroup={createImageGroupForPdp(product?.c_sidecardImage)}
                            imageUrl={
                                product?.c_sidecardImage
                                    ? `${baseUrl}/${product.c_sidecardImage}`
                                    : null
                            }
                            imageAlt={''}
                            textGroupPdp={{
                                bodyCopy: generateDescriptionWithBullets(product),
                                expandCtaTitle: formatMessage({
                                    defaultMessage: 'Read more',
                                    id: 'sidecard.read_more',
                                }),
                            }}
                            type={'pdp'}
                            imagePosition="right"
                            hasPaddings={true}
                            backgroundColor="white"
                            button={null}
                        />
                    )}

                    {/* <Flex flexDir={isBraun ? 'column-reverse' : 'column'} data-ab="tech-spec">
                        <Box ref={specificationsRef}>
                            <AccordionBlock title={specificationsTitle} items={accordionData} />
                        </Box>
                    </Flex> */}

                    {/*
                    {product?.c_youtubeVideoGeneric &&
                        (() => {
                            const selectedVideo = parseAndSanitizeJSONWithSpaces(
                                product?.c_youtubeVideoGeneric,
                                product?.c_subModelIdentifier,
                            )?.[1]

                            if (!selectedVideo || !selectedVideo.videolink) return null

                            return (
                                <Box
                                    style={{
                                        width: '100%',
                                        maxWidth: remCalc(1440),
                                        margin: `${remCalc(40)} auto`,
                                        aspectRatio: 2 / 1,
                                    }}
                                    data-ab="editorial-video-generic"
                                    data-cs-label="editorial-video-generic"
                                >
                                    <YoutubeIframe
                                        videoSrc={selectedVideo.videolink}
                                        title={selectedVideo.title}
                                    />
                                </Box>
                            )
                        })()}
                    {product?.c_award && (
                        <Box ref={awardsRef} data-ab="editorial-awards" data-cs-label="editorial-awards">
                            <Awards awards={product.c_award} p={product?.c_subModelIdentifier} />
                        </Box>
                    )}

                    {product?.c_benefit && !isBraun && (
                        <Box ref={benefitsRef} data-ab={'editorial-benefit'} data-cs-label="editorial-benefit">
                            {parseAndSanitizeJSONWithSpaces(
                                product.c_benefit,
                                product?.c_subModelIdentifier,
                            )?.map((benefit, index) => {
                                return (
                                    <SideCard
                                        key={index}
                                        imageGroup={createImageGroupForPdp(benefit?.imageLink)}
                                        imageUrl={`${baseUrl}/${benefit?.imageLink}`}
                                        imageAlt={''}
                                        textGroupPdp={{
                                            title: benefit?.title,
                                            bodyCopy: benefit?.description,
                                        }}
                                        type={'pdp'}
                                        imagePosition={index % 2 !== 0 ? 'right' : 'left'}
                                        hasPaddings={true}
                                        backgroundColor="white"
                                        button={null}
                                        mobileFullSizePdp={true}
                                        titleAs="h2"
                                    />
                                )
                            })}
                        </Box>
                    )}

                    {product?.[isBraun ? 'c_benefit' : 'c_keyFeature'] && (
                        <Box>
                            <Box
                                ref={keyFeaturesRef}
                                data-ab="editorial-key-features"
                                data-cs-label="editorial-key-features"
                            >
                                <KeyFeatures
                                    categoryName={product?.c_categoryName}
                                    cards={featureCards}
                                    isSlider
                                    aspectRatio="4/3"
                                />
                            </Box>
                        </Box>
                    )} */}
                    {/*
                    {product?.c_keyFeature && isBraun && (
                        <Features features={product?.c_keyFeature} />
                    )}

                    <Flex flexDir={isBraun ? 'column-reverse' : 'column'}>
                        <Box
                            layerStyle="page"
                            display={{md: 'flex'}}
                            justifyContent={{md: 'center'}}
                        >
                            <ProductDocumentation product={product} />
                        </Box>
                    </Flex>

                    {product?.c_youtubeVideoHowTo && (
                        <PromoSliderVideo
                            abTitle="editorial-howto"
                            sliderGroup={parseAndSanitizeJSONWithSpaces(
                                product?.c_youtubeVideoHowTo,
                                product?.c_subModelIdentifier,
                            )?.map((video) => ({
                                text_block: {
                                    title: video.title,
                                },
                                video_link: {
                                    href: video.videolink,
                                },
                                thumbnail: video.medialink ? {src: video.medialink} : {},
                            }))}
                            contentType="howto"
                        />
                    )}
                    <Box layerStyle="page" data-ab="compare" data-cs-label="compare">
                        <ProductCompare product={product} />
                    </Box> */}

                    {[
                        editorialSourceProduct?.c_disclaimerText,
                        editorialSourceProduct?.c_disclaimerText2,
                    ]
                        .filter(Boolean)
                        .map((text, idx) => (
                            <Box
                                key={idx}
                                layerStyle="page"
                                paddingY={{
                                    base: remCalc(8),
                                    md: remCalc(16),
                                    lg: remCalc(24),
                                    xl: remCalc(40),
                                }}
                                data-ab="disclaimer"
                                data-cs-label="disclaimer"
                            >
                                <SanitizedHtml
                                    as={Text}
                                    html={text}
                                    textStyle="body-medium"
                                    color="text.default"
                                    sanitize={true}
                                />
                            </Box>
                        ))}
                </Fragment>
            </Stack>
            <div
                data-ab="reviews"
                data-cs-label="reviews"
                data-bv-show="reviews"
                data-bv-product-id={
                    product?.c_subModelIdentifier
                        ? product.c_subModelIdentifier
                              .replace(/\s+/g, '_')
                              .replace(/:/g, '_')
                              .replace(/\+/g, '_')
                              .replace(/\//g, '_')
                        : undefined
                }
                style={{margin: 'auto', maxWidth: remCalc(1432)}}
            ></div>
            {wazzupConfig && product && (
                <div
                    className="w-product"
                    code={variant?.productId || product.id}
                    name={product.name}
                    price={
                        variant?.price ||
                        getPriceData(product)?.salePrice ||
                        getPriceData(product)?.regularPrice ||
                        ''
                    }
                    url={`https://${customParams.currentHost}${buildLocalizedUrl(
                        location.pathname,
                    )}`}
                    image={schemaImageLink || ''}
                />
            )}
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object,
}

export default ProductDetail
